/**
 * Leitura do conteudo dos arquivos do bucket.
 *
 * Porte de gcs-reader/web/js/app.js:95-184. Os arquivos AF_* vem com um
 * preambulo de metadados antes do cabecalho real, e o delimitador varia entre
 * ';', ',' e tabulacao — por isso a deteccao e feita por amostragem em vez de
 * assumir um formato fixo.
 */

export type FileContentType = 'json' | 'csv' | 'text';

export interface ParsedTable {
  headers: string[];
  rows: string[][];
  delimiter: string;
}

export function detectFileType(content: string, path: string): FileContentType {
  if (path.endsWith('.json')) { return 'json'; }
  if (path.endsWith('.csv')) { return 'csv'; }
  if (path.endsWith('.txt') || path.endsWith('.log')) { return 'text'; }

  // Sem extensao conhecida, tenta reconhecer a estrutura
  if (content.includes(',') || content.includes('\t')) {
    const lines = content.split('\n').slice(0, 3);
    if (lines.some(line => (line.match(/,/g) || []).length > 2)) { return 'csv'; }
  }

  return 'text';
}

/**
 * Detecta o delimitador e o cabecalho de um conteudo delimitado.
 * Devolve null quando o conteudo nao tem cara de tabela.
 */
export function parseTable(content: string): ParsedTable | null {
  if (!content) { return null; }

  const lines = content.replace(/\r\n/g, '\n').split('\n');
  while (lines.length && lines[lines.length - 1].trim() === '') { lines.pop(); }
  if (lines.length === 0) { return null; }

  const candidates = [';', ',', '\t'];
  const countDelimiter = (line: string, delimiter: string) => line.split(delimiter).length - 1;

  // Dez linhas bastam para decidir o delimitador sem varrer arquivos grandes
  const sample = lines.filter(line => line.trim() !== '').slice(0, 10);

  let bestDelimiter: string = null;
  let bestModeCount = 0;
  let bestScore = 0;

  candidates.forEach(delimiter => {
    const counts = sample.map(line => countDelimiter(line, delimiter)).filter(count => count > 0);
    if (!counts.length) { return; }

    const frequency: { [count: string]: number } = {};
    counts.forEach(count => { frequency[count] = (frequency[count] || 0) + 1; });

    const entries = Object.keys(frequency)
      .map(count => ({ count: Number(count), frequency: frequency[count] }))
      .sort((a, b) => b.frequency - a.frequency);
    if (!entries.length) { return; }

    // Vence o delimitador que aparece muitas vezes e em muitas linhas
    const score = entries[0].count * entries[0].frequency;
    if (score > bestScore) {
      bestScore = score;
      bestDelimiter = delimiter;
      bestModeCount = entries[0].count;
    }
  });

  if (!bestDelimiter) { return null; }

  // A primeira linha com a contagem dominante e o cabecalho: e isso que descarta o preambulo
  const headerIndex = lines.findIndex(line => countDelimiter(line, bestDelimiter) === bestModeCount);
  if (headerIndex === -1) { return null; }

  const dataLines = lines.slice(headerIndex).filter(line => line.trim() !== '');
  if (dataLines.length < 2) { return null; }

  const splitLine = (line: string) => {
    const cells = line.split(bestDelimiter);
    if (cells.length > 1 && cells[cells.length - 1] === '') { cells.pop(); }
    return cells.map(cell => cell.trim());
  };

  return {
    headers: splitLine(dataLines[0]),
    rows: dataLines.slice(1).map(splitLine),
    delimiter: bestDelimiter
  };
}

export function tableToJson(table: ParsedTable): Array<{ [header: string]: string }> {
  if (!table) { return null; }

  return table.rows.map(row => {
    const item: { [header: string]: string } = {};
    table.headers.forEach((header, index) => { item[header] = row[index] || ''; });
    return item;
  });
}

/** Formata o conteudo como json, caindo para a tabela detectada quando nao for json. */
export function toPrettyJson(content: string, table: ParsedTable): string {
  try {
    return JSON.stringify(JSON.parse(content), null, 2);
  } catch {
    const fromTable = tableToJson(table);
    return fromTable && fromTable.length ? JSON.stringify(fromTable, null, 2) : null;
  }
}

export function isJson(content: string): boolean {
  try {
    JSON.parse(content);
    return true;
  } catch {
    return false;
  }
}
